import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { Play, Square, RefreshCw } from 'lucide-react';
import * as api from '../../../../services/api/timeReportingApi';
import * as projectsApi from '../../../../services/api/projectsApi';
import * as S from '../../Common/internalToolsStyles';

const formatElapsed = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const TimerWidget = ({ onTimerChange, todayMinutes = 0 }) => {
  const { companyId } = useParams();
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [timerError, setTimerError] = useState(null);
  const [showSwitch, setShowSwitch] = useState(false);
  const [switchProject, setSwitchProject] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    projectsApi.getMyProjects(companyId)
      .then(res => setProjects(res.data.projects || []))
      .catch(() => {});
  }, [companyId]);

  const checkActiveTimer = useCallback(async () => {
    try {
      const res = await api.getActiveTimer(companyId);
      if (res.status === 204 || !res.data?.timer) {
        setActiveTimer(null);
        return;
      }
      setActiveTimer(res.data.timer);
    } catch {
      setActiveTimer(null);
    }
  }, [companyId]);

  useEffect(() => { checkActiveTimer(); }, [checkActiveTimer]);

  useEffect(() => {
    const pollId = setInterval(checkActiveTimer, 30000);
    return () => clearInterval(pollId);
  }, [checkActiveTimer]);

  // A row in active_timer always represents a running timer — no end_time column.
  const timerIsRunning = !!activeTimer;
  const startTimeStr = activeTimer?.started_at;
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timerIsRunning && startTimeStr) {
      // BUG FIX 2026-05-12: backend tenant pool serves TIMESTAMP columns as
      // bare strings ("2026-05-12 08:30:45", UTC, no TZ marker — see
      // middlewares/middlewares.js:303 `dateStrings: true, timezone: 'Z'`).
      // Passing that to `new Date(...)` triggers the browser's non-ISO
      // parser which interprets as LOCAL time, shifting the start by the
      // host's TZ offset (e.g. -2h on CEST). The elapsed counter then
      // jumped to 02:00:00 the instant the timer started. Append 'Z' so
      // it parses as UTC regardless of browser TZ.
      const startMs = new Date(startTimeStr.replace(' ', 'T') + 'Z').getTime();
      const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000));
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setElapsed(0);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timerIsRunning, startTimeStr]);

  const handleStart = async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await api.startTimer(companyId, {
        projectId: selectedProject.value,
        description: description || null,
      });
      setActiveTimer(res.data.timer);
      setDescription('');
      setTimerError(null);
      onTimerChange?.();
    } catch (err) {
      setTimerError(err.response?.data?.message || 'Kunde inte starta timer');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await api.stopTimer(companyId);
      setActiveTimer(null);
      setTimerError(null);
      setShowSwitch(false);
      onTimerChange?.();
    } catch (err) {
      setTimerError(err.response?.data?.message || 'Kunde inte stoppa timer');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = async () => {
    if (!switchProject) return;
    setLoading(true);
    try {
      const res = await api.switchTimer(companyId, {
        projectId: switchProject.value,
        description: description || null,
      });
      // switchTimer returns { stoppedEntry, newTimer }
      setActiveTimer(res.data.newTimer);
      setDescription('');
      setTimerError(null);
      setShowSwitch(false);
      setSwitchProject(null);
      onTimerChange?.();
    } catch (err) {
      setTimerError(err.response?.data?.message || 'Kunde inte byta projekt');
    } finally {
      setLoading(false);
    }
  };

  const projectOptions = projects.map(p => ({
    value: p.project_id,
    label: `${p.code ? p.code + ' — ' : ''}${p.name}`,
    color: p.color,
  }));

  const isRunning = !!activeTimer;
  const isOvertime = elapsed > 8 * 3600;

  // Today total = already-logged minutes (from parent) + currently-running session
  const todayTotalMinutes = todayMinutes + (isRunning ? Math.floor(elapsed / 60) : 0);
  const todayTotalHours = Math.round((todayTotalMinutes / 60) * 10) / 10;

  return (
    <div>
      {timerError && (
        <div style={{ fontSize: 12, color: S.colors.danger, marginBottom: 8, padding: '4px 0' }}>{timerError}</div>
      )}

      {isRunning ? (
        <div>
          {/* Running state — responsive: row on desktop, stacked on mobile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: S.colors.success,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <span style={{
                fontVariantNumeric: 'tabular-nums', fontSize: 22, fontWeight: 700,
                color: isOvertime ? S.colors.warning : S.colors.textPrimary,
              }}>
                {formatElapsed(elapsed)}
              </span>
              {isOvertime && (
                <span style={{ fontSize: 10, color: S.colors.warning, fontWeight: 600 }}>8h+</span>
              )}
              {todayTotalMinutes > 0 && (
                <span
                  title="Totalt loggat idag (inkl. pågående timer)"
                  style={{
                    fontSize: 11,
                    color: S.colors.textMuted,
                    padding: '3px 8px',
                    background: S.colors.bgHover,
                    borderRadius: 99,
                    marginLeft: 4,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  Idag: {todayTotalHours}h
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
              {activeTimer.project_color && (
                <span style={S.colorDot(activeTimer.project_color, 10)} />
              )}
              <span style={{ fontSize: 13, color: S.colors.textSecondary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeTimer.project_name || 'Projekt'}
              </span>
              {activeTimer.description && (
                <span style={{ fontSize: 12, color: S.colors.textMuted }}>
                  — {activeTimer.description}
                </span>
              )}
            </div>
            {/* gap=16 to clear the Stop button's halo pulse (radiates ~14px
                outside the button). Anything tighter and the halo visually
                grazes/swallows the adjacent Byt button. */}
            <div style={{ display: 'flex', gap: 16, marginLeft: 'auto', flexShrink: 0, alignItems: 'center' }}>
              <button
                onClick={() => setShowSwitch(!showSwitch)}
                disabled={loading}
                aria-label="Byt projekt"
                title="Byt projekt"
                style={{ ...S.btnSecondary, padding: '8px 12px' }}
              >
                <RefreshCw size={14} />
                <span className="d-none d-sm-inline">Byt</span>
              </button>
              {/* Round red stop button — same ring+circle pattern as the
                  idle Play button (outline 2px + outline-offset 2px). A
                  second halo-pulse animation radiates outward from the
                  static ring to signal "timer is live, click to stop";
                  this is a soft box-shadow expansion, layered on top of
                  the static outline rather than replacing it.
                  Pulse pauses on hover so the brightness shift reads
                  cleanly without competing motion. */}
              <button
                onClick={handleStop}
                disabled={loading}
                aria-label="Stoppa timer"
                title="Stoppa timer"
                className="timer-btn-running"
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  border: 'none', padding: 0, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  // Brand token from styles.css, not S.colors.danger (which is
                  // an internal-tools palette with a different hue).
                  background: 'var(--bs-danger)',
                  color: 'white',
                  outline: '2px solid var(--bs-danger)',
                  outlineOffset: '2px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'filter 150ms ease',
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
              >
                <Square size={14} fill="white" />
              </button>
            </div>
          </div>

          {/* Switch project row */}
          {showSwitch && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
              padding: '10px 12px', background: S.colors.bgSubtle, borderRadius: 8, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 12, color: S.colors.textMuted, fontWeight: 500 }}>Byt till:</span>
              <div style={{ flex: 1, minWidth: 160 }}>
                <Select
                  options={projectOptions.filter(p => p.value !== activeTimer.project_id)}
                  value={switchProject}
                  onChange={setSwitchProject}
                  placeholder="Välj nytt projekt..."
                  isClearable
                  aria-label="Välj nytt projekt"
                  menuPortalTarget={document.body}
                  theme={brandSelectTheme}
                  styles={{
                    control: (base) => ({ ...base, minHeight: 34, fontSize: 13 }),
                    option: (base) => ({ ...base, fontSize: 13 }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
              <button
                onClick={handleSwitch}
                disabled={loading || !switchProject}
                style={{
                  ...S.btnPrimary,
                  background: switchProject ? S.colors.success : S.colors.textDisabled,
                  padding: '6px 14px', fontSize: 13,
                }}
              >
                Byt
              </button>
              <button onClick={() => { setShowSwitch(false); setSwitchProject(null); }} style={{ ...S.btnGhost, padding: '6px 8px' }}>
                ✕
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Idle state — responsive. Leading Clock icon removed 2026-05-12 in
           favor of a round play button at the trailing edge: the play CTA
           is itself the timer affordance, so a separate clock icon was
           redundant. Today-badge becomes the leading element when present;
           otherwise the row starts with the project picker, which keeps
           the row visually anchored on the action rather than a decoration. */
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {todayMinutes > 0 && (
            <span
              title="Totalt loggat idag"
              style={{
                fontSize: 11,
                color: S.colors.textMuted,
                padding: '3px 8px',
                background: S.colors.bgHover,
                borderRadius: 99,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Idag: {Math.round((todayMinutes / 60) * 10) / 10}h
            </span>
          )}
          <div style={{ flex: 1, minWidth: 160 }}>
            <Select
              options={projectOptions}
              value={selectedProject}
              onChange={setSelectedProject}
              placeholder="Välj projekt..."
              isClearable
              aria-label="Välj projekt för timer"
              menuPortalTarget={document.body}
              theme={brandSelectTheme}
              styles={{
                control: (base) => ({ ...base, minHeight: 36, fontSize: 13 }),
                option: (base) => ({ ...base, fontSize: 13 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Beskrivning (valfritt)"
            aria-label="Beskrivning för timer"
            onKeyDown={e => { if (e.key === 'Enter' && selectedProject) handleStart(); }}
            style={{
              flex: 1, minWidth: 120,
              padding: '7px 10px', fontSize: 13, borderRadius: 6,
              border: `1px solid ${S.colors.borderDefault}`, outline: 'none',
            }}
          />
          {/* Round play button — solid colored circle with a thin ring offset
              outside, matching the lönekörning .pl-step-indicator.viewing
              pattern (outline + outline-offset). The ring uses the same hue
              as the fill so it reads as a coherent affordance rather than a
              border. Disabled state suppresses the ring entirely so the
              button visually recedes when no project is picked.
              36px matches the input/select row height. Brightness shift on
              hover; no transform-scale (no layout jitter). */}
          <button
            onClick={handleStart}
            disabled={loading || !selectedProject}
            aria-label="Starta timer"
            title="Starta timer"
            className="timer-btn-idle"
            style={{
              width: 36, height: 36, borderRadius: '50%',
              border: 'none', padding: 0, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              // Use the canonical brand tokens from styles.css rather than
              // the internalToolsStyles standalone palette — those have
              // their own values (#047857 etc.) that don't match the rest
              // of the app's `var(--bs-*)`-driven UI.
              background: selectedProject ? 'var(--bs-success)' : S.colors.textDisabled,
              color: 'white',
              outline: selectedProject ? '2px solid var(--bs-success)' : 'none',
              outlineOffset: selectedProject ? '2px' : '0',
              cursor: selectedProject && !loading ? 'pointer' : 'not-allowed',
              transition: 'filter 150ms ease, background 200ms ease, outline-color 200ms ease',
            }}
            onMouseEnter={(e) => { if (selectedProject && !loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
          >
            {/* Play icon optically centered — the triangle's visual mass sits
                left of its bounding box, so a small marginLeft compensates.
                Stop button (Square) doesn't need this; squares are symmetric. */}
            <Play size={14} fill="white" style={{ marginLeft: 2 }} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

        /* Halo-pulse for the running-state Stop button. The button already
           carries a static 2px ring at 2px offset (≈4px outside the edge);
           the halo pulse starts AT that ring's outer boundary so it reads
           as a soft expansion of the existing ring, not a separate inner
           shadow. Same idiom as the lönekörning .pl-step-indicator.current
           but tuned so the static-ring + halo layers compose cleanly.
           RGB matches styles.css --bs-danger (#f72b50). */
        @keyframes timer-halo-danger {
          0%   { box-shadow: 0 0 0 4px rgba(247, 43, 80, 0.40); }
          70%  { box-shadow: 0 0 0 14px rgba(247, 43, 80, 0); }
          100% { box-shadow: 0 0 0 4px rgba(247, 43, 80, 0); }
        }
        .timer-btn-running {
          animation: timer-halo-danger 2s ease-out infinite;
        }
        /* Pause the halo on hover so the brightness shift reads cleanly. */
        .timer-btn-running:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
};

export default TimerWidget;
